import React from "react";
import { NavLink, Link } from "react-router-dom";
import { FaWhatsapp, FaUser, FaBook, FaCalendar, FaBars, FaXmark, FaGraduationCap } from "react-icons/fa6";
import { useAuth } from "../auth/useAuth.hook";
import api from "../utils/axiosConfig";

const Header: React.FC = () => {
  const { logout, accessToken } = useAuth();
  const [agentName, setAgentName] = React.useState<string>("");
  const displayName = React.useMemo(() => (agentName ? agentName.split(" ")[0] : ""), [agentName]);

  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    let isMounted = true;
    const fetchMe = async () => {
      if (!accessToken) return;
      try {
        const { data } = await api.get(`agents/me`);
        if (isMounted && data?.name) setAgentName(data.name as string);
      } catch (e) {
        // noop
      }
    };
    fetchMe();
    return () => {
      isMounted = false;
    };
  }, [accessToken]);

  // Support WhatsApp config
  const supportPhone = (import.meta as any).env?.VITE_WHATSAPP_SUPPORT_NUMBER ?? "5541996477018";
  const supportMsg = encodeURIComponent("Olá! Preciso de suporte.");
  const supportUrl = `https://api.whatsapp.com/send?phone=${supportPhone}&text=${supportMsg}`;

  const linkBase =
    "flex items-center gap-2 px-2 py-1 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-white/60 whitespace-nowrap";

  return (
    <>
    <header
      role="banner"
      className={`fixed top-0 inset-x-0 z-50 h-20 md:h-24 p-4 bg-gradient-to-r from-blue-500 to-indigo-600 ${
        scrolled ? "shadow-md" : "shadow"
      }`}
    >
      <div className="container mx-auto h-full flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="bg-white dark:bg-gray-900 p-2 rounded-lg">
            <img
              src="/logo-preppo.png"
              alt="Preppo"
              className="h-12 w-auto md:h-16"
            />
          </Link>
        </div>

        {/* Desktop navigation */}
        <nav
          className="hidden md:flex items-center gap-5"
          aria-label="Principal"
        >
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              `${linkBase} ${isActive ? "text-white bg-white/10" : "text-white/90 hover:text-white"}`
            }
          >
            <FaBook /> <span>Meu painel</span>
          </NavLink>
          <NavLink
            to="/history"
            className={({ isActive }) =>
              `${linkBase} ${isActive ? "text-white bg-white/10" : "text-white/90 hover:text-white"}`
            }
          >
            <FaCalendar /> <span>Audiências e pagamentos</span>
          </NavLink>
          <NavLink
            to="/courses"
            className={({ isActive }) =>
              `${linkBase} ${isActive ? "text-white bg-white/10" : "text-white/90 hover:text-white"}`
            }
          >
            <FaGraduationCap /> <span>Cursos</span>
          </NavLink>
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              `${linkBase} ${isActive ? "text-white bg-white/10" : "text-white/90 hover:text-white"}`
            }
          >
            <FaUser /> <span>Meus dados</span>
          </NavLink>
          <a
            href={supportUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`${linkBase} text-white/90 hover:text-white`}
          >
            <FaWhatsapp /> <span>Suporte</span>
          </a>
        </nav>

        <div className="flex items-center gap-3">
          {/* Greeting placeholder to avoid layout shifts */}
          <div className="hidden md:block text-white/90 min-w-[140px] text-right">
            {displayName && (
              <span>
                Olá, <span className="font-semibold text-white">{displayName}</span>!
              </span>
            )}
          </div>
          <button
            className="hidden md:inline-flex items-center border border-white/50 text-white px-4 py-2 rounded-lg hover:bg-white/10 hover:border-white/70 transition-colors focus:outline-none focus:ring-2 focus:ring-white/60"
            onClick={logout}
          >
            Sair
          </button>

          {/* Mobile menu toggle */}
          <button
            className="md:hidden inline-flex items-center justify-center text-white/90 hover:text-white p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-white/60"
            aria-label="Abrir menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <FaXmark size={20} /> : <FaBars size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu content */}
      {open && (
        <div className="md:hidden mt-3 container mx-auto">
          <div className="flex flex-col gap-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 p-3 shadow">
            {displayName && (
              <div className="text-white/90 pb-1">
                Olá, <span className="font-semibold text-white">{displayName}</span>!
              </div>
            )}
            <NavLink
              to="/dashboard"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? "text-white bg-white/10" : "text-white/90 hover:text-white"}`
              }
            >
              <FaBook /> <span>Meu painel</span>
            </NavLink>
            <NavLink
              to="/history"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? "text-white bg-white/10" : "text-white/90 hover:text-white"}`
              }
            >
              <FaCalendar /> <span>Audiências e pagamentos</span>
            </NavLink>
            <NavLink
              to="/courses"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? "text-white bg-white/10" : "text-white/90 hover:text-white"}`
              }
            >
              <FaGraduationCap /> <span>Cursos</span>
            </NavLink>
            <NavLink
              to="/profile"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? "text-white bg-white/10" : "text-white/90 hover:text-white"}`
              }
            >
              <FaUser /> <span>Meus dados</span>
            </NavLink>
            <a
              href={supportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`${linkBase} text-white/90 hover:text-white`}
              onClick={() => setOpen(false)}
            >
              <FaWhatsapp /> <span>Suporte</span>
            </a>
            <button
              className="mt-1 inline-flex items-center border border-white/50 text-white px-4 py-2 rounded-lg hover:bg-white/10 hover:border-white/70 transition-colors"
              onClick={() => {
                setOpen(false);
                logout();
              }}
            >
              Sair
            </button>
          </div>
        </div>
      )}
    </header>
    {/* Spacer to offset fixed header height */}
    <div className="h-20 md:h-24" aria-hidden />
    </>
  );
};

export default Header;
